import { useCallback, useEffect, useRef, useState } from 'react';
import { Banner, BlockStack, Button, InlineStack, Modal, Select, Text } from '@shopify/polaris';
import { useAuth } from '../../App';
import { useApi } from '../../hooks/useApi';

type Copy = {id: number; generated_at: string; generated_by_name: string | null; available: boolean};
type History = {copies: Copy[]; next: number | null};
type Preview = {html: string; version: string};
type Result<T> = {data: T};

export function DeliveryNote({orderId}: {orderId: number}) {
  const {user, currentShop, currentOrganization, hasPermission} = useAuth();
  const [open, setOpen] = useState(false);
  if (!hasPermission('orders.view')) return null;
  return <>
    <Button onClick={() => setOpen(true)}>Print delivery note</Button>
    {open && <DeliveryNoteDialog key={JSON.stringify([user?.id, currentOrganization?.id, currentShop?.id, orderId])}
      orderId={orderId} canSave={hasPermission('orders.edit')} onClose={() => setOpen(false)} />}
  </>;
}

function DeliveryNoteDialog({orderId, canSave, onClose}: {orderId: number; canSave: boolean; onClose: () => void}) {
  const {fetchApi, error} = useApi();
  const path = '/orders/' + orderId + '/delivery-note';
  const [selection, setSelection] = useState('current');
  const [history, setHistory] = useState<History>({copies: [], next: null});
  const [preview, setPreview] = useState<Preview | null>(null);
  const [busy, setBusy] = useState(true);
  const [ready, setReady] = useState(false);
  const [message, setMessage] = useState('');
  const frame = useRef<HTMLIFrameElement>(null);
  const requestId = useRef('');
  const saving = useRef(false);
  const reloadHistory = useCallback(async (before?: number) => {
    const result = await fetchApi<Result<History>>(path + '/saved' + (before ? '?before=' + before : ''));
    if (result) setHistory(old => ({copies: before ? [...old.copies, ...result.data.copies] : result.data.copies, next: result.data.next}));
  }, [path, fetchApi]);
  useEffect(() => { void reloadHistory(); }, [reloadHistory]);
  useEffect(() => {
    let current = true;
    const source = path + (selection === 'current' ? '' : '/saved/' + selection) + '?format=preview';
    setBusy(true);
    setReady(false);
    setPreview(null);
    setMessage('');
    requestId.current = crypto.randomUUID();
    void fetchApi<Result<{html: string; version?: string}>>(source).then(result => {
      if (!current) return;
      if (result) setPreview({html:result.data.html, version:result.data.version || ''});
      setBusy(false);
    });
    return () => { current = false; };
  }, [fetchApi, path, selection]);

  const save = async () => {
    if (!preview || selection !== 'current' || !canSave || saving.current) return;
    saving.current = true;
    setBusy(true);
    setMessage('');
    try {
      const result = await fetchApi<Result<{id:number}>>(path, {method:'POST', body:JSON.stringify({
        request_id:requestId.current, expected_version:preview.version,
      })});
      if (result) {
        await reloadHistory();
        setSelection(String(result.data.id));
      }
    } finally {
      saving.current = false;
      setBusy(false);
    }
  };
  const print = () => {
    try { frame.current?.contentWindow?.focus(); frame.current?.contentWindow?.print(); }
    catch { setMessage('Allow printing for this page, then try again.'); }
  };
  return <Modal open onClose={onClose} title="Delivery note" size="large">
    <Modal.Section><BlockStack gap="300">
      <Select label="Version" value={selection} disabled={busy} onChange={setSelection} options={[
        {value:'current', label:'Current order'},
        ...history.copies.map(copy => ({value:String(copy.id), disabled:!copy.available,
          label:'Copy #' + copy.id + ' · ' + copy.generated_at + ' UTC' + (copy.generated_by_name ? ' · ' + copy.generated_by_name : '') + (copy.available ? '' : ' (no verified file)')})),
      ]} />
      {history.next && <Button disabled={busy} onClick={async () => {
        setBusy(true); try { await reloadHistory(history.next!); } finally { setBusy(false); }
      }}>Show older copies</Button>}
      {error && <Banner tone="critical">{error}</Banner>}
      {message && <Text as="p" aria-live="polite">{message}</Text>}
      <InlineStack gap="200">
        <Button variant="primary" disabled={!ready || !preview || busy} onClick={print}>Print or save PDF</Button>
        {canSave && selection === 'current' && <Button disabled={!preview || busy} onClick={save}>
          Save a copy in Whenly
        </Button>}
      </InlineStack>
      {busy && !preview && <Text as="p">Loading delivery note…</Text>}
      {preview && <iframe ref={frame} title="Delivery note print preview" srcDoc={preview.html}
        sandbox="allow-same-origin allow-modals" onLoad={() => setReady(true)} style={{width:'100%',height:'60vh',border:0}} />}
    </BlockStack></Modal.Section>
  </Modal>;
}
